import { z } from 'zod'

/**
 * Whiteboard sync (PLAN-080). A drawing is a flat set of Excalidraw elements
 * owned by its DrawingStore Durable Object. Clients send the elements they
 * changed (whole element JSON); the DO keeps the newest version of each —
 * the same per-element last-writer-wins rule Excalidraw's own collab server
 * applies (`version`, tie broken by the lower `versionNonce`) — and
 * broadcasts what it accepted. Pointer/selection presence is volatile.
 *
 * Element shape is Excalidraw's; only the fields the store needs are typed.
 */

export const drawingElementSchema = z
  .object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    versionNonce: z.number().int(),
    isDeleted: z.boolean().optional(),
    /** Fractional z-order index (`a0`, `a1`, …) — Excalidraw ≥ 0.17. */
    index: z.string().nullable().optional(),
  })
  .passthrough()
export type DrawingElement = z.infer<typeof drawingElementSchema>

/** File (image) attached to a drawing; the bytes live in R2. */
export const drawingFileSchema = z.object({
  id: z.string().min(1),
  mimeType: z.string().min(1),
  /** Epoch ms, as Excalidraw's `BinaryFileData.created`. */
  created: z.number(),
  size: z.number().int().nonnegative(),
})
export type DrawingFile = z.infer<typeof drawingFileSchema>

export const drawingPointerSchema = z.object({
  x: z.number(),
  y: z.number(),
  tool: z.enum(['pointer', 'laser']),
})

export const drawingPresenceSchema = z.object({
  /** Connection id — one user may have several tabs open. */
  connId: z.string(),
  actor: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  pointer: drawingPointerSchema.optional(),
  button: z.enum(['up', 'down']).optional(),
  selectedElementIds: z.array(z.string()).optional(),
})
export type DrawingPresence = z.infer<typeof drawingPresenceSchema>

// ── Client → server ───────────────────────────────────────────────────────

export const drawingClientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('sync') }),
  z.object({ t: z.literal('elements'), elements: z.array(drawingElementSchema).max(2_000) }),
  z.object({
    t: z.literal('presence'),
    pointer: drawingPointerSchema.optional(),
    button: z.enum(['up', 'down']).optional(),
    selectedElementIds: z.array(z.string()).max(500).optional(),
  }),
])
export type DrawingClientMessage = z.infer<typeof drawingClientMessageSchema>

// ── Server → client ───────────────────────────────────────────────────────

export const drawingServerMessageSchema = z.discriminatedUnion('t', [
  /**
   * Full scene. Large scenes arrive in several chunks (`chunk.i` of
   * `chunk.n`); `files`/`presence`/`self` are only on the first one.
   */
  z.object({
    t: z.literal('snapshot'),
    rev: z.number(),
    self: z.string().optional(),
    elements: z.array(drawingElementSchema),
    files: z.array(drawingFileSchema).optional(),
    presence: z.array(drawingPresenceSchema).optional(),
    chunk: z.object({ i: z.number(), n: z.number() }),
  }),
  /** Elements changed by someone else (or the server's newer copy of ours). */
  z.object({
    t: z.literal('elements'),
    rev: z.number(),
    actor: z.string(),
    elements: z.array(drawingElementSchema),
  }),
  z.object({ t: z.literal('files'), files: z.array(drawingFileSchema) }),
  z.object({ t: z.literal('presence'), presence: z.array(drawingPresenceSchema) }),
  z.object({ t: z.literal('error'), message: z.string() }),
])
export type DrawingServerMessage = z.infer<typeof drawingServerMessageSchema>

/** Workspace path (relative to `drawings/`) for a drawing file's bytes. */
export function drawingFileRel(drawingId: string, fileId: string): string {
  return `${drawingId}/files/${fileId}`
}

/**
 * Excalidraw's rule for which copy of an element wins: higher version, then
 * lower versionNonce on a tie. `true` when `incoming` should replace `current`.
 */
export function elementIsNewer(
  incoming: { version: number; versionNonce: number },
  current: { version: number; versionNonce: number } | null | undefined
): boolean {
  if (!current) return true
  if (incoming.version !== current.version) return incoming.version > current.version
  return incoming.versionNonce < current.versionNonce
}
